import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, ArrowRight, BookOpen, LifeBuoy, Loader2, ShieldAlert } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  BANKING_DECLINED_LINE,
  CONDITIONAL_RESIGNATION_LINE,
  MEMBER_QUESTION,
  NOT_A_MEMBER_STOP,
  OFFICE_HELPER,
  ORIENTATION_DO,
  ORIENTATION_KNOW,
  ORIENTATION_SCHEDULE,
  SEQUENCING_TEACHING,
  SPOUSE_POSTURE_TEACHING,
  SPOUSE_QUESTION,
  routeAppointment,
  todayIso,
  type AppointWorkflowData,
  type YesNo,
} from './appointContent';

interface AppointPhase1Props {
  task: any;
  phaseData?: any;
  onPhaseComplete: () => void;
}

const STEP_TITLES = ['Start here', 'Is the person a Member?', 'One more question', 'The appointment', 'Review'];

function AskAPerson() {
  return (
    <Button asChild variant="outline" size="sm" className="min-h-11">
      <a href="/support/ask">
        <LifeBuoy className="h-4 w-4 mr-2" />
        Ask a person
      </a>
    </Button>
  );
}

function YesNoField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value?: YesNo;
  onChange: (v: YesNo) => void;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <RadioGroup value={value} onValueChange={(v) => onChange(v as YesNo)} className="flex gap-6">
        <div className="flex items-center gap-2">
          <RadioGroupItem value="yes" id={`${id}-yes`} />
          <Label htmlFor={`${id}-yes`} className="font-normal cursor-pointer">
            Yes
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="no" id={`${id}-no`} />
          <Label htmlFor={`${id}-no`} className="font-normal cursor-pointer">
            No
          </Label>
        </div>
      </RadioGroup>
    </div>
  );
}

export function AppointPhase1({ task, onPhaseComplete }: AppointPhase1Props) {
  const { toast } = useToast();
  const existing: AppointWorkflowData = task?.workflow_data?.appointment || {};

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [unaName, setUnaName] = useState('');

  const [isMember, setIsMember] = useState<YesNo | undefined>(existing.is_member);
  const [isSpouse, setIsSpouse] = useState<YesNo | undefined>(existing.is_spouse);
  const [personName, setPersonName] = useState(existing.person_name || '');
  const [office, setOffice] = useState(existing.office || '');
  const [effectiveDate, setEffectiveDate] = useState(existing.effective_date || todayIso());
  const [grantBanking, setGrantBanking] = useState<YesNo | undefined>(existing.grant_banking);
  const [bankingAccounts, setBankingAccounts] = useState(existing.banking_accounts || '');
  const [notes, setNotes] = useState(existing.notes || '');

  useEffect(() => {
    async function loadUna() {
      if (!task?.una_id) return;
      const { data } = await supabase.from('unas').select('name').eq('id', task.una_id).maybeSingle();
      if (data?.name) setUnaName(data.name);
    }
    loadUna();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.una_id]);

  const data: AppointWorkflowData = useMemo(
    () => ({
      is_member: isMember,
      is_spouse: isSpouse,
      person_name: personName,
      office,
      effective_date: effectiveDate,
      grant_banking: grantBanking,
      banking_accounts: bankingAccounts,
      notes,
    }),
    [isMember, isSpouse, personName, office, effectiveDate, grantBanking, bankingAccounts, notes],
  );

  const route = routeAppointment(data);
  const notAMember = isMember === 'no';

  async function persist() {
    setSaving(true);
    try {
      const nextData = { ...(task.workflow_data || {}), appointment: data };
      await supabase.from('task_workflows').update({ workflow_data: nextData }).eq('task_id', task.id);
      if (task.workflow_data) task.workflow_data.appointment = data;
    } catch (e: any) {
      console.error('Could not save the appointment answers', e);
      toast({
        title: 'We could not save that',
        description: e.message || 'Please try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }

  function canContinue(): boolean {
    if (step === 2) return isMember === 'yes';
    if (step === 3) return !!isSpouse;
    if (step === 4) {
      if (notAMember) return false;
      if (!personName.trim() || !office.trim() || !effectiveDate) return false;
      if (!grantBanking) return false;
      if (grantBanking === 'yes' && !bankingAccounts.trim()) return false;
      return true;
    }
    return true;
  }

  async function next() {
    await persist();
    setStep((s) => Math.min(5, s + 1));
  }

  async function finish() {
    await persist();
    toast({ title: 'Saved', description: 'Your answers are saved. Next you prepare the papers.' });
    onPhaseComplete();
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-base">
              Step {step} of 5: {STEP_TITLES[step - 1]}
            </CardTitle>
            {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {step === 1 && (
            <div className="space-y-4 text-sm leading-relaxed">
              <div>
                <h4 className="font-medium mb-1">What you need to know</h4>
                <p>{ORIENTATION_KNOW}</p>
              </div>
              <div>
                <h4 className="font-medium mb-1">What you need to do</h4>
                <p>{ORIENTATION_DO}</p>
              </div>
              <div>
                <h4 className="font-medium mb-1">Your schedule</h4>
                <p>{ORIENTATION_SCHEDULE}</p>
              </div>
              <Button asChild variant="outline" size="sm" className="min-h-11">
                <a href="/support/library">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Read the officer guides in the library
                </a>
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <YesNoField id="is-member" label={MEMBER_QUESTION} value={isMember} onChange={setIsMember} />
              {notAMember && (
                <Alert className="border-destructive/50">
                  <ShieldAlert className="h-4 w-4" />
                  <AlertDescription className="space-y-3 text-sm">
                    <p>{NOT_A_MEMBER_STOP}</p>
                    <AskAPerson />
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <YesNoField id="is-spouse" label={SPOUSE_QUESTION} value={isSpouse} onChange={setIsSpouse} />
              {isSpouse === 'yes' && (
                <Alert>
                  <AlertDescription className="text-sm leading-relaxed">{SPOUSE_POSTURE_TEACHING}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="appt-person">Person's name</Label>
                  <Input id="appt-person" value={personName} onChange={(e) => setPersonName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="appt-date">Effective date</Label>
                  <Input
                    id="appt-date"
                    type="date"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="appt-office">The office</Label>
                <Input id="appt-office" value={office} onChange={(e) => setOffice(e.target.value)} />
                <p className="text-sm text-muted-foreground">{OFFICE_HELPER}</p>
              </div>
              <YesNoField
                id="grant-banking"
                label="Grant banking authority now?"
                value={grantBanking}
                onChange={setGrantBanking}
              />
              {grantBanking === 'yes' && (
                <div className="space-y-2">
                  <Label htmlFor="appt-accounts">Which accounts</Label>
                  <Textarea
                    id="appt-accounts"
                    value={bankingAccounts}
                    onChange={(e) => setBankingAccounts(e.target.value)}
                    rows={3}
                  />
                </div>
              )}
              {grantBanking === 'no' && (
                <Alert>
                  <AlertDescription className="text-sm">{BANKING_DECLINED_LINE}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5 text-sm">
              {unaName && <p className="text-muted-foreground">These papers prepare for {unaName}.</p>}
              <div className="space-y-2">
                <Label htmlFor="appt-notes">Anything the record should say</Label>
                <Textarea id="appt-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
              </div>
              <Alert>
                <AlertDescription className="space-y-2 text-sm">
                  <p>{route.summary}</p>
                  <p>{SEQUENCING_TEACHING}</p>
                  <p>{CONDITIONAL_RESIGNATION_LINE}</p>
                </AlertDescription>
              </Alert>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            <Button
              variant="outline"
              className="min-h-11"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            {step < 5 ? (
              <Button onClick={next} disabled={!canContinue() || saving} className="min-h-11">
                Continue
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={finish} disabled={saving || route.hardStop} className="min-h-11">
                Save and go to the papers
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default AppointPhase1;
